/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useCallback, useEffect, useState } from "react";
import { AuthProvider, useFetchTokens } from "features/auth";
import { useNavigate } from "react-router-dom";
import { useFetchUser } from "features/users";
import type { AuthUser } from "aws-amplify/auth";
import { Loading } from "@components";
import { useNotification } from "@hooks/useNotifications";
import { isUnauthorizedError } from "@libs/utils";
import { Unauthorized } from "./Unauthorized";

export const Authenticated = ({
  signOut,
  user,
  children,
}: {
  signOut: (callback?: VoidFunction) => void;
  user: AuthUser;
  children: JSX.Element;
}) => {
  const { showError } = useNotification();
  const navigate = useNavigate();
  const { userTokens, isLoading, refetch: refetchTokens } = useFetchTokens(user);
  const [skipFetchUser, setSkipFetchUser] = useState(true); // Fetch user only after the tokens are fetched
  const {
    user: userData,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUser,
  } = useFetchUser(user?.signInDetails?.loginId ?? "", skipFetchUser);

  const onSignOut = useCallback(() => {
    localStorage.clear();

    signOut();
  }, [signOut]);

  useEffect(() => {
    if (userTokens) {
      setSkipFetchUser(false);
      refetchUser();
    }
  }, [userTokens]);

  const handleUnauthorized = () => {
    if (isLoading || userError) {
      return;
    }

    showError("You are not authorized to access this page.");
    navigate("/");

    refetchTokens();
  };

  useEffect(() => {
    document.addEventListener("axios:unauthorized", handleUnauthorized);

    return () => {
      document.removeEventListener("axios:unauthorized", handleUnauthorized);
    };
  });

  // Logout the user if the user is unauthorized
  useEffect(() => {
    if (isUnauthorizedError(userError?.response?.status)) {
      onSignOut();
    }
  }, [userError]);

  if (isLoading || isUserLoading || skipFetchUser) {
    return <Loading />;
  }

  if (!userData || !userTokens) {
    return <Unauthorized onSignOut={onSignOut} userError={userError} userTokens={userTokens} />;
  }

  return (
    <AuthProvider user={userData} signOut={onSignOut}>
      {children}
    </AuthProvider>
  );
};
